import React from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';
import { Bot, CheckCircle, X, User, Clock } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { format } from 'date-fns';

export default function PendingBotApprovalsCard({ officers, currentUser }) {
  const queryClient = useQueryClient();

  // Filter for pending bot approvals
  const pendingBots = officers.filter(o => 
    o.is_bot && 
    o.bot_approval_status === 'pending'
  );

  const approveBotMutation = useMutation({
    mutationFn: (officerId) => base44.entities.OfficerProfile.update(officerId, {
      bot_approval_status: 'approved',
      bot_approved_by: currentUser?.email,
      bot_approved_at: new Date().toISOString()
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['officers'] });
    }
  });

  const rejectBotMutation = useMutation({
    mutationFn: (officerId) => base44.entities.OfficerProfile.update(officerId, {
      bot_approval_status: 'rejected'
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['officers'] });
    }
  });

  const getOfficerByEmail = (email) => officers.find(o => o.user_email === email);

  if (pendingBots.length === 0) {
    return null;
  }

  return (
    <Card className="rounded-lg border bg-purple-50 border-purple-500/30">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Bot className="w-5 h-5 text-purple-500" />
            <div className="text-left">
              <CardTitle className="text-slate-900 font-semibold text-base">Bot Officer Requests</CardTitle>
              <p className="text-xs text-slate-500">AI Co-workers pending approval</p>
            </div>
          </div>
          <Badge className="bg-purple-500/20 text-purple-500 border-none">
            {pendingBots.length}
          </Badge>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-3 max-h-60 overflow-y-auto">
          {pendingBots.map((bot) => {
            const owner = getOfficerByEmail(bot.bot_owner_email);
            const requester = getOfficerByEmail(bot.bot_approval_requested_by);
            
            return (
              <div key={bot.id} className="p-3 bg-white rounded-lg border border-slate-200 hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-indigo-500 flex items-center justify-center flex-shrink-0">
                      <Bot className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-slate-900">🤖 {bot.callsign}</h4>
                      <p className="text-xs text-slate-500">{bot.rank_title}</p>
                      
                      <div className="mt-2 space-y-1">
                        <div className="flex items-center gap-2 text-xs">
                          <User className="w-3 h-3 text-slate-400" />
                          <span className="text-slate-500">Owner:</span>
                          <span className="text-slate-700 font-medium">
                            {owner?.callsign || bot.bot_owner_email?.split('@')[0] || 'Unknown'}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 text-xs">
                          <Clock className="w-3 h-3 text-slate-400" />
                          <span className="text-slate-500">Requested by:</span>
                          <span className="text-slate-700 font-medium">
                            {requester?.callsign || bot.bot_approval_requested_by?.split('@')[0] || 'Unknown'}
                          </span>
                          {bot.bot_approval_requested_at && (
                            <span className="text-slate-400">
                              • {format(new Date(bot.bot_approval_requested_at), 'MMM d, HH:mm')}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="flex gap-2 mt-3">
                  <Button 
                    size="sm" 
                    className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700"
                    onClick={() => approveBotMutation.mutate(bot.id)}
                    disabled={approveBotMutation.isPending}
                  >
                    <CheckCircle className="w-3 h-3 mr-1" /> Approve
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline"
                    className="h-7 text-xs border-red-500/30 text-red-500 hover:bg-red-50"
                    onClick={() => rejectBotMutation.mutate(bot.id)}
                    disabled={rejectBotMutation.isPending}
                  >
                    <X className="w-3 h-3 mr-1" /> Reject
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}